import { useEffect, useRef } from 'react';

type AllowedLangs = Record<string, string>;

interface UseGoogleTranslateProps {
  pageLanguage?: string;
  allowedLangs?: AllowedLangs;
  storageKey?: string;
  selectId: string;
  translateElementId: string;
}

export const useGoogleTranslate = ({
  pageLanguage = 'nl',
  allowedLangs = { en: 'English', nl: 'Dutch' },
  storageKey = 'goog_selected_lang',
  selectId,
  translateElementId
}: UseGoogleTranslateProps) => {
  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return; // run only once
    initializedRef.current = true;

    const langSelect = document.getElementById(
      selectId
    ) as HTMLSelectElement | null;
    if (!langSelect) return;

    // Determine initial language
    const savedLang = localStorage.getItem(storageKey);
    const browserLang = (navigator.language || 'en').split('-')[0];
    const initialLang =
      savedLang || (allowedLangs[browserLang] ? browserLang : pageLanguage);

    // Populate dropdown
    Object.entries(allowedLangs).forEach(([code, label]) => {
      const opt = document.createElement('option');
      opt.value = code;
      opt.textContent = label;
      if (code === initialLang) opt.selected = true;
      langSelect.appendChild(opt);
    });

    // Set Google Translate cookie
    const setGoogleCookie = (lang: string) => {
      const expires = new Date(
        Date.now() + 365 * 24 * 60 * 60 * 1000
      ).toUTCString();
      document.cookie = `googtrans=/${pageLanguage}/${lang};path=/;expires=${expires};SameSite=Lax`;
      localStorage.setItem(storageKey, lang);
    };

    // Apply translation
    const applyTranslation = (lang: string) => {
      setGoogleCookie(lang);

      const tryTranslate = (attempts = 0) => {
        const combo = document.querySelector(
          '.goog-te-combo'
        ) as HTMLSelectElement | null;
        if (combo) {
          combo.value = '';
          combo.dispatchEvent(new Event('change', { bubbles: true }));

          setTimeout(() => {
            combo.value = lang;
            combo.dispatchEvent(new Event('change', { bubbles: true }));
            if (combo.value !== lang && attempts < 50) {
              setTimeout(() => tryTranslate(attempts + 1), 200);
            }
          }, 100);
        } else if (attempts < 50) {
          setTimeout(() => tryTranslate(attempts + 1), 200);
        }
      };

      tryTranslate();
    };

    // Listen to dropdown changes
    langSelect.addEventListener('change', () =>
      applyTranslation(langSelect.value)
    );

    // Google Translate global init
    (window as any).googleTranslateElementInit = () => {
      new (window as any).google.translate.TranslateElement(
        {
          pageLanguage,
          includedLanguages: Object.keys(allowedLangs).join(','),
          autoDisplay: false
        },
        translateElementId
      );

      if (!localStorage.getItem(storageKey)) {
        localStorage.setItem(storageKey, initialLang);
      }
      applyTranslation(initialLang);
    };

    // Load Google Translate script only once
    if (!document.getElementById('google-translate-script')) {
      const script = document.createElement('script');
      script.id = 'google-translate-script';
      script.src = `//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit`;
      script.async = true;
      document.body.appendChild(script);
    }
  }, [allowedLangs, pageLanguage, selectId, storageKey, translateElementId]);
};
